import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import buildingImg from '../assets/building.png';
import { supabase } from '../supabaseClient';
import { useBrochure } from '../hooks/useBrochure';
import SEO from '../components/SEO';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
  const [loadingFaqs, setLoadingFaqs] = useState(true);
  const [openFaqId, setOpenFaqId] = useState(null);
  const brochureUrl = useBrochure();

  useEffect(() => {
    fetchProjects();
    fetchBlogs();
    fetchFaqs();
  }, []);

  const fetchBlogs = async () => {
    setLoadingBlogs(true);
    const { data, error } = await supabase.from('blogs').select('*').order('created_at', { ascending: false }).limit(3);
    if (!error) setBlogs(data || []);
    setLoadingBlogs(false);
  };

  const fetchFaqs = async () => {
    setLoadingFaqs(true);
    const { data, error } = await supabase.from('faqs').select('*').order('created_at', { ascending: false });
    if (!error) setFaqs(data || []);
    setLoadingFaqs(false);
  };

  const toggleFaq = (id) => {
    setOpenFaqId(prev => prev === id ? null : id);
  };

  const fetchProjects = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('projects').select('*').order('created_at', { ascending: false });
    if (error) {
      console.error('Error fetching projects:', error);
    } else {
      setProjects(data || []);
    }
    setLoading(false);
  };

  const ongoingProjects = projects.filter(p => p.status === 'ONGOING');
  const completedProjects = projects.filter(p => p.status === 'COMPLETED');

  return (
    <div className="bg-gray-50 min-h-screen w-full font-sans">
      <SEO 
        title="Exclusive Real Estate Projects & Properties | HBB0011 REALTY"
        description="Browse our portfolio of ongoing and completed luxury real estate projects. Find your dream investment with HBB0011 REALTY."
        keywords="luxury real estate projects, premium properties, real estate portfolio, HBB0011 REALTY properties"
      />
      
      {/* 1. Hero Section */}
      <header className="relative w-full pt-28 pb-16 md:pt-32 md:pb-20 lg:min-h-[85vh] bg-[#041424] overflow-hidden flex items-center">
        {/* Background Image Blend */}
        <div className="absolute inset-0 z-0 flex justify-center">
          <div className="w-full lg:w-3/5 h-full relative ml-auto">
            <img 
              src={buildingImg} 
              alt="Luxury real estate project twilight view" 
              className="w-full h-full object-cover opacity-30 lg:opacity-50 mix-blend-luminosity"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#041424] via-[#041424]/80 lg:via-transparent to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/80 lg:via-[#041424]/40 to-transparent"></div>
          </div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-12">
          
          {/* Left: Hero Content */}
          <div className="w-full lg:w-3/5 space-y-4 sm:space-y-6 text-center lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-2 text-xs sm:text-sm font-bold tracking-widest mb-2 sm:mb-4">
              <Link to="/" className="text-white hover:text-[#D4AF37] transition-colors uppercase">Home</Link>
              <span className="text-gray-400">&gt;</span>
              <span className="text-[#D4AF37] uppercase">Projects</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-2 tracking-tight">
              OUR PROJECTS
            </h1>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-[#D4AF37] font-medium leading-tight mb-4 sm:mb-6">
              BUILT ON TRUST.<br className="hidden sm:block"/>DELIVERED WITH EXCELLENCE.
            </h2>
            <p className="text-gray-300 max-w-lg mx-auto lg:mx-0 text-sm sm:text-base md:text-lg leading-relaxed">
              From iconic landmarks to premium communities, each project we deliver reflects our commitment to quality, innovation, and customer satisfaction.
            </p>
          </div>

          {/* Right: Responsive Stats (Grid on mobile, Column on desktop) */}
          <div className="w-full lg:w-1/4 grid grid-cols-2 gap-6 sm:gap-8 lg:flex lg:flex-col lg:items-end mt-8 lg:mt-0">
            <div className="flex flex-col sm:flex-row items-center lg:justify-end gap-2 sm:gap-4 text-center sm:text-left">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl">domain</span>
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white">1+</h3>
                <p className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-widest">Projects</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center lg:justify-end gap-2 sm:gap-4 text-center sm:text-left">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl">location_city</span>
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white">1+</h3>
                <p className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-widest">Landmarks</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center lg:justify-end gap-2 sm:gap-4 text-center sm:text-left">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl">architecture</span>
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white">80K+</h3>
                <p className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-widest">Sq. Ft.<br className="hidden sm:block"/>Developed</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-center lg:justify-end gap-2 sm:gap-4 text-center sm:text-left">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl">public</span>
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white">5+</h3>
                <p className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-widest">Cities</p>
              </div>
            </div>
          </div>

        </div>
      </header>

      {/* 2. Filter Tabs - Horizontally Scrollable on Mobile */}
      <section className="w-full bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-x-auto no-scrollbar">
          <div className="flex items-center min-w-max">
            
            <button className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-4 sm:py-5 bg-[#041424] text-white font-bold text-[10px] sm:text-xs uppercase tracking-widest whitespace-nowrap">
              <span className="material-symbols-outlined text-base sm:text-lg">apartment</span>
              All Projects
            </button>
            <button className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-4 sm:py-5 text-gray-500 hover:text-[#041424] font-bold text-[10px] sm:text-xs uppercase tracking-widest transition-colors whitespace-nowrap">
              <span className="material-symbols-outlined text-base sm:text-lg">workspace_premium</span>
              Signature Projects
            </button>
            <button className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-4 sm:py-5 text-gray-500 hover:text-[#041424] font-bold text-[10px] sm:text-xs uppercase tracking-widest transition-colors whitespace-nowrap">
              <span className="material-symbols-outlined text-base sm:text-lg">construction</span>
              Ongoing Projects
            </button>
            <button className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-4 sm:py-5 text-gray-500 hover:text-[#041424] font-bold text-[10px] sm:text-xs uppercase tracking-widest transition-colors whitespace-nowrap">
              <span className="material-symbols-outlined text-base sm:text-lg">check_circle</span>
              Completed Projects
            </button>
            <button className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-4 sm:py-5 text-gray-500 hover:text-[#041424] font-bold text-[10px] sm:text-xs uppercase tracking-widest transition-colors whitespace-nowrap">
              <span className="material-symbols-outlined text-base sm:text-lg">trending_up</span>
              Investments
            </button>

          </div>
        </div>
      </section>

      <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24 lg:space-y-32">
        
        {/* 3. Signature Project */}
        <section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start lg:items-center">
            
            {/* Left: Project Info */}
            <div className="lg:col-span-3 order-2 lg:order-1">
              <h4 className="text-[#D4AF37] uppercase text-[10px] sm:text-xs font-bold tracking-widest mb-1 sm:mb-2">Our Signature Project</h4>
              <h2 className="text-2xl sm:text-3xl text-[#041424] font-bold mb-2">SIGNATURE TOWER</h2>
              <p className="text-gray-500 flex items-center gap-1 text-xs sm:text-sm mb-4 sm:mb-6 font-medium">
                <span className="material-symbols-outlined text-gray-400 text-sm sm:text-base">location_on</span>
                Location To Be Announced
              </p>
              
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                An iconic commercial landmark that redefines business spaces with modern architecture, world-class amenities, and unmatched connectivity.
              </p>
              
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                <li className="flex items-center gap-2 text-xs sm:text-sm text-[#041424] font-medium">
                  <span className="material-symbols-outlined text-[#D4AF37] text-base sm:text-lg">check_circle</span>
                  20 Cent Landmark
                </li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-[#041424] font-medium">
                  <span className="material-symbols-outlined text-[#D4AF37] text-base sm:text-lg">check_circle</span>
                  G+7 Floors
                </li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-[#041424] font-medium">
                  <span className="material-symbols-outlined text-[#D4AF37] text-base sm:text-lg">check_circle</span>
                  Premium Commercial Spaces
                </li>
                <li className="flex items-center gap-2 text-xs sm:text-sm text-[#041424] font-medium">
                  <span className="material-symbols-outlined text-[#D4AF37] text-base sm:text-lg">check_circle</span>
                  Vaastu Compliant
                </li>
              </ul>

              <div className="flex flex-col sm:flex-row gap-3">
                <button className="bg-[#041424] text-white px-4 sm:px-5 py-3 sm:py-4 lg:py-3 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-[#062038] transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
                  View Details <span className="material-symbols-outlined text-sm">arrow_forward</span>
                </button>
                {brochureUrl ? (
                  <a href={brochureUrl} target="_blank" rel="noopener noreferrer" className="border border-[#041424] text-[#041424] px-4 sm:px-5 py-3 sm:py-4 lg:py-3 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
                    Brochure <span className="material-symbols-outlined text-sm">download</span>
                  </a>
                ) : (
                  <button className="border border-[#041424] text-[#041424] px-4 sm:px-5 py-3 sm:py-4 lg:py-3 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto cursor-not-allowed opacity-50">
                    Brochure <span className="material-symbols-outlined text-sm">download</span>
                  </button>
                )}
              </div>
            </div>

            {/* Center: Image */}
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="w-full h-64 sm:h-80 md:h-96 lg:h-[500px] rounded-xl overflow-hidden shadow-2xl relative">
                <img src={buildingImg} alt="Signature Tower" className="w-full h-full object-cover" />
              </div>
            </div>

            {/* Right: Vertical Stats (Grid on mobile, Column on desktop) */}
            <div className="lg:col-span-2 order-3 lg:order-3 grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-col lg:justify-center gap-6 lg:gap-8 pt-6 lg:pt-0 pl-0 lg:pl-6 border-t lg:border-t-0 border-l-0 lg:border-l border-gray-200">
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-2 lg:gap-4">
                <span className="material-symbols-outlined text-[#041424] text-2xl sm:text-3xl font-light">aspect_ratio</span>
                <div>
                  <h5 className="text-[#041424] font-bold text-xs sm:text-sm">2.5+</h5>
                  <p className="text-[10px] sm:text-xs text-gray-500">Lakh Sq. Ft.<br className="hidden lg:block"/> Total</p>
                </div>
              </div>
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-2 lg:gap-4">
                <span className="material-symbols-outlined text-[#041424] text-2xl sm:text-3xl font-light">domain</span>
                <div>
                  <h5 className="text-[#041424] font-bold text-xs sm:text-sm">G+7</h5>
                  <p className="text-[10px] sm:text-xs text-gray-500">Floors</p>
                </div>
              </div>
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-2 lg:gap-4">
                <span className="material-symbols-outlined text-[#041424] text-2xl sm:text-3xl font-light">local_parking</span>
                <div>
                  <h5 className="text-[#041424] font-bold text-xs sm:text-sm">4 Level</h5>
                  <p className="text-[10px] sm:text-xs text-gray-500">Parking</p>
                </div>
              </div>
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-2 lg:gap-4">
                <span className="material-symbols-outlined text-[#041424] text-2xl sm:text-3xl font-light">star</span>
                <div>
                  <h5 className="text-[#041424] font-bold text-xs sm:text-sm">100%</h5>
                  <p className="text-[10px] sm:text-xs text-gray-500">Vaastu</p>
                </div>
              </div>
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-2 lg:gap-4">
                <span className="material-symbols-outlined text-[#041424] text-2xl sm:text-3xl font-light">event_available</span>
                <div>
                  <h5 className="text-[#041424] font-bold text-xs sm:text-sm">2025</h5>
                  <p className="text-[10px] sm:text-xs text-gray-500">Approved</p>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* 4. Ongoing Projects */}
        <section>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8 border-b border-gray-200 pb-4">
            <h3 className="text-lg sm:text-xl font-bold text-[#D4AF37] uppercase tracking-wider">ONGOING PROJECTS</h3>
          </div>
          {loading ? (
            <p className="text-center text-gray-500 py-12">Loading ongoing projects...</p>
          ) : ongoingProjects.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center shadow-sm">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl mb-3">folder_off</span>
              <h4 className="text-base sm:text-lg font-bold text-[#041424] uppercase tracking-widest mb-1">No Ongoing Projects Available Right Now</h4>
              <p className="text-gray-500 text-xs sm:text-sm">Please check back soon for updates on our upcoming developments.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {ongoingProjects.map(project => (
                <div key={project.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 flex flex-col">
                  <div className="h-48 w-full bg-gray-200 relative">
                    <img src={project.image_url || buildingImg} alt={project.title} className="w-full h-full object-cover" />
                    {project.location && (
                      <div className="absolute top-4 left-4 bg-black/60 backdrop-blur text-white px-3 py-1 rounded text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm text-[#D4AF37]">location_on</span>
                        {project.location}
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h4 className="text-xl font-bold text-[#041424] mb-2 uppercase tracking-wide">{project.title}</h4>
                    {project.price && <p className="text-[#D4AF37] font-bold mb-3">{project.price}</p>}
                    <p className="text-gray-600 text-sm mb-4 flex-1">{project.description}</p>
                    <div className="flex flex-col gap-2 w-full mt-auto">
                      <button className="border border-[#041424] text-[#041424] px-4 py-2 rounded-sm font-bold text-xs uppercase tracking-widest hover:bg-[#041424] hover:text-white transition-colors w-full">
                        View Details
                      </button>
                      {brochureUrl && (
                        <a href={brochureUrl} target="_blank" rel="noopener noreferrer" className="bg-[#041424] text-white px-4 py-2 rounded-sm font-bold text-xs uppercase tracking-widest hover:bg-[#D4AF37] hover:text-[#041424] transition-colors w-full flex justify-center items-center gap-2">
                          Brochure <span className="material-symbols-outlined text-sm">download</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 5. Completed Projects */}
        <section>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8 border-b border-gray-200 pb-4">
            <h3 className="text-lg sm:text-xl font-bold text-[#D4AF37] uppercase tracking-wider">COMPLETED PROJECTS</h3>
          </div>
          {loading ? (
            <p className="text-center text-gray-500 py-12">Loading completed projects...</p>
          ) : completedProjects.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center shadow-sm">
              <span className="material-symbols-outlined text-[#D4AF37] text-4xl mb-3">folder_off</span>
              <h4 className="text-base sm:text-lg font-bold text-[#041424] uppercase tracking-widest mb-1">No Completed Projects Available Right Now</h4>
              <p className="text-gray-500 text-xs sm:text-sm">Please contact us for more information about our past deliveries.</p>
            </div>
          ) : (
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {completedProjects.map(project => (
                <div key={project.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 flex flex-col">
                  <div className="h-48 w-full bg-gray-200 relative">
                    <img src={project.image_url || buildingImg} alt={project.title} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500" />
                    {project.location && (
                      <div className="absolute top-4 left-4 bg-black/60 backdrop-blur text-white px-3 py-1 rounded text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm text-[#D4AF37]">location_on</span>
                        {project.location}
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h4 className="text-xl font-bold text-[#041424] mb-2 uppercase tracking-wide">{project.title}</h4>
                    {project.price && <p className="text-gray-500 font-medium mb-3">{project.price}</p>}
                    <p className="text-gray-600 text-sm mb-4 flex-1">{project.description}</p>
                    <div className="flex flex-col gap-2 w-full mt-auto">
                      <button className="border border-[#041424] text-[#041424] px-4 py-2 rounded-sm font-bold text-xs uppercase tracking-widest hover:bg-[#041424] hover:text-white transition-colors w-full">
                        View Details
                      </button>
                      {brochureUrl && (
                        <a href={brochureUrl} target="_blank" rel="noopener noreferrer" className="bg-[#041424] text-white px-4 py-2 rounded-sm font-bold text-xs uppercase tracking-widest hover:bg-[#D4AF37] hover:text-[#041424] transition-colors w-full flex justify-center items-center gap-2">
                          Brochure <span className="material-symbols-outlined text-sm">download</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

      </main>

      {/* 6. Investment Opportunities */}
      <section className="bg-white pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8 border-b border-gray-200 pb-4">
            <h3 className="text-lg sm:text-xl font-bold text-[#D4AF37] uppercase tracking-wider">INVESTMENT OPPORTUNITIES</h3>
            <Link to="#" className="text-[#041424] text-xs sm:text-sm font-bold flex items-center gap-1 hover:text-[#D4AF37] transition-colors">
              View All <span className="material-symbols-outlined text-sm sm:text-base">arrow_forward</span>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            
            <div className="bg-[#041424] rounded-lg p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-4 shadow-xl">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light">trending_up</span>
              <div>
                <h4 className="text-white font-bold text-xs sm:text-sm uppercase tracking-widest mb-2">High Growth Locations</h4>
                <p className="text-gray-400 text-[10px] sm:text-xs leading-relaxed mb-4">Invest in rapidly developing locations with high appreciation potential.</p>
                <Link to="#" className="text-[#D4AF37] text-[10px] sm:text-xs font-bold uppercase tracking-widest flex items-center gap-1 hover:text-white transition-colors">
                  Learn More <span className="material-symbols-outlined text-xs sm:text-sm">arrow_forward</span>
                </Link>
              </div>
            </div>

            <div className="bg-[#041424] rounded-lg p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-4 shadow-xl">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light">monetization_on</span>
              <div>
                <h4 className="text-white font-bold text-xs sm:text-sm uppercase tracking-widest mb-2">Attractive Returns</h4>
                <p className="text-gray-400 text-[10px] sm:text-xs leading-relaxed mb-4">Curated investment options designed for maximum returns.</p>
                <Link to="#" className="text-[#D4AF37] text-[10px] sm:text-xs font-bold uppercase tracking-widest flex items-center gap-1 hover:text-white transition-colors">
                  Learn More <span className="material-symbols-outlined text-xs sm:text-sm">arrow_forward</span>
                </Link>
              </div>
            </div>

            <div className="bg-[#041424] rounded-lg p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-4 shadow-xl sm:col-span-2 lg:col-span-1">
              <span className="material-symbols-outlined text-[#D4AF37] text-3xl sm:text-4xl font-light">verified_user</span>
              <div>
                <h4 className="text-white font-bold text-xs sm:text-sm uppercase tracking-widest mb-2">Safe & Secure Investments</h4>
                <p className="text-gray-400 text-[10px] sm:text-xs leading-relaxed mb-4">Transparent process, legal compliance & trusted by hundreds of investors.</p>
                <Link to="#" className="text-[#D4AF37] text-[10px] sm:text-xs font-bold uppercase tracking-widest flex items-center gap-1 hover:text-white transition-colors">
                  Learn More <span className="material-symbols-outlined text-xs sm:text-sm">arrow_forward</span>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 6.5 Latest Blogs & News */}
      <section className="bg-gray-50 pb-16 sm:pb-20 border-t border-gray-200 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8 border-b border-gray-200 pb-4">
            <h3 className="text-lg sm:text-xl font-bold text-[#D4AF37] uppercase tracking-wider">LATEST UPDATES & NEWS</h3>
            <Link to="/blog" className="text-[#041424] text-xs sm:text-sm font-bold flex items-center gap-1 hover:text-[#D4AF37] transition-colors">
              View All News <span className="material-symbols-outlined text-sm sm:text-base">arrow_forward</span>
            </Link>
          </div>
          
          {loadingBlogs ? (
            <p className="text-center text-gray-500 py-4">Loading news...</p>
          ) : blogs.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No recent updates.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {blogs.map(blog => (
                <div key={blog.id} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 flex flex-col hover:shadow-xl transition-shadow group">
                  <div className="h-48 w-full relative bg-gray-200 overflow-hidden">
                    <img src={blog.image_url || buildingImg} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-[#D4AF37] text-[10px] sm:text-xs mb-3 font-medium tracking-wider uppercase">
                      <span className="material-symbols-outlined text-sm">calendar_month</span>
                      {new Date(blog.created_at).toLocaleDateString()}
                    </div>
                    <h4 className="text-lg font-bold text-[#041424] mb-3 leading-tight group-hover:text-[#D4AF37] transition-colors line-clamp-2">{blog.title}</h4>
                    <p className="text-gray-600 text-xs leading-relaxed mb-6 flex-grow line-clamp-3">{blog.content}</p>
                    <Link to="/blog" className="text-[#D4AF37] text-[10px] sm:text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:text-[#041424] transition-colors mt-auto w-fit">
                      Read More <span className="material-symbols-outlined text-sm">arrow_forward</span>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 6.6 FAQs */}
      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-xl sm:text-2xl font-bold text-[#041424] uppercase tracking-wider mb-2">Frequently Asked Questions</h3>
            <div className="w-16 h-1 bg-[#D4AF37] mx-auto"></div>
          </div>
          
          {loadingFaqs ? (
            <p className="text-center text-gray-500 py-4">Loading FAQs...</p>
          ) : faqs.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No FAQs available at the moment.</p>
          ) : (
            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                  <button
                    onClick={() => toggleFaq(faq.id)}
                    className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-bold text-[#041424] pr-4">{faq.question}</span>
                    <span className={`material-symbols-outlined text-[#D4AF37] transition-transform duration-300 shrink-0 ${openFaqId === faq.id ? 'rotate-180' : ''}`}>
                      expand_more
                    </span>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openFaqId === faq.id ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="p-5 sm:p-6 pt-0 text-gray-600 text-sm sm:text-base border-t border-gray-100 whitespace-pre-wrap">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 7. CTA Section */}
      <section className="bg-[#041424] py-12 sm:py-16 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
          
          <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-4 sm:gap-6">
            <div className="hidden sm:flex w-14 sm:w-16 h-14 sm:h-16 shrink-0 rounded-sm border border-[#D4AF37]/30 items-center justify-center text-[#D4AF37]">
              <span className="material-symbols-outlined text-3xl sm:text-4xl font-light">domain_add</span>
            </div>
            <div>
              <h4 className="text-[#D4AF37] text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-2">Let's Build Your Next Success</h4>
              <h3 className="text-2xl sm:text-3xl text-white font-light mb-2">Book a Site Visit & Explore Our Projects</h3>
              <p className="text-gray-400 text-xs sm:text-sm">Experience our projects in person. Our experts are here to guide you.</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full lg:w-auto shrink-0">
            <button className="bg-[#D4AF37] text-[#041424] px-4 sm:px-6 py-3 sm:py-4 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-white transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              Book a Site Visit
              <span className="material-symbols-outlined text-sm sm:text-base">calendar_month</span>
            </button>
            <button className="border border-white text-white px-4 sm:px-6 py-3 sm:py-4 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-white hover:text-[#041424] transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              Talk to an Expert
              <span className="material-symbols-outlined text-sm sm:text-base">call</span>
            </button>
          </div>

        </div>
      </section>

    </div>
  );
};

export default Projects;